import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import {
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material'
import { Helmet } from '@dr.pogodin/react-helmet'
import KeywordsGrid from './KeywordsGrid'
import PRFilterKeywordsApi from '../../../api/PRFilterKeywordsApi'
import { FactsTable, FactsTableRow } from '../../components/FactsTable'
import { RegisteredCheckBox } from '../../../components/fields/CheckBox'
import { RegisteredTextField } from '../../../components/fields/TextField'
import { PRFilterKeyword } from 'types/PRFilterKeyword'

const prFilterKeywordsApi = new PRFilterKeywordsApi()

function AdminKeywordFilterView() {
    const [keywords, setKeywords] = useState<PRFilterKeyword[]>([])
    const [open, setOpen] = useState(false)
    const formMethods = useForm({ defaultValues: { language: '', phrase: '', negation: false } })

    async function fetchData() {
        const keywords = await prFilterKeywordsApi.findAllFilterKeywords()
        const sortedKeywords = keywords.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
        setKeywords(sortedKeywords)
    }

    useEffect(() => {
        void fetchData()
    }, [])

    const handleOpenDialog = () => {
        setOpen(true)
    }

    const handleCloseDialog = () => {
        setOpen(false)
    }

    const handleSubmitForm = async () => {
        const formData = formMethods.getValues()
        await prFilterKeywordsApi.createFilterKeyword({ ...formData, field: 'title' })
        formMethods.reset()
        setOpen(false)
        void fetchData()
    }

    const handleDeleteKeyword = async (keywordId: string) => {
        await prFilterKeywordsApi.deleteFilterKeyword(keywordId)
        const updatedKeywords = keywords.filter((keyword) => keyword.id !== keywordId)
        setKeywords(updatedKeywords)
    }

    const handleUpdateKeyword = async (keyword: PRFilterKeyword) => {
        const response = await prFilterKeywordsApi.updateFilterKeyword(keyword)
        if (response) {
            formMethods.reset()
            setKeywords((prevKeywords) => prevKeywords.map((k) => (k.id === keyword.id ? { ...k, ...keyword } : k)))
            setOpen(false)
        }
    }

    return (
        <>
            <Helmet>
                <title>Keyword filter</title>
            </Helmet>
            <Typography
                variant='h3'
                gutterBottom
                sx={{
                    display: 'block',
                }}
            >
                Keywords {keywords ? `(${keywords.length})` : ''}
            </Typography>
            <Divider sx={{ my: 6 }} />
            <Card sx={{ mb: 6 }}>
                <CardContent>
                    <Button variant='contained' color='primary' onClick={handleOpenDialog} sx={{ mb: 4 }}>
                        Lägg till nytt keyword
                    </Button>
                    <Dialog open={open} onClose={handleCloseDialog}>
                        <DialogTitle>Lägg till keyword</DialogTitle>
                        <DialogContent sx={{ paddingBottom: 0 }}>
                            <DialogContentText>
                                To subscribe to this website, please enter your email address here. We will send updates
                                occasionally.
                            </DialogContentText>
                            <FormProvider {...formMethods}>
                                <FactsTable>
                                    <FactsTableRow label='Språk'>
                                        <FormControl fullWidth sx={{ mt: 2 }}>
                                            <InputLabel id='select-label'>Språk</InputLabel>
                                            <Select
                                                labelId='select-label'
                                                name='language'
                                                label='Språk'
                                                fullWidth
                                                onChange={(e) => {
                                                    const value = e.target.value
                                                    formMethods.setValue('language', value as 'sv' | 'en')
                                                }}
                                            >
                                                <MenuItem value='' disabled>
                                                    Välj språk
                                                </MenuItem>
                                                <MenuItem value='sv'>sv</MenuItem>
                                                <MenuItem value='en'>en</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </FactsTableRow>
                                    <FactsTableRow label='Fras'>
                                        <RegisteredTextField label='Fras' name='phrase' fullWidth />
                                    </FactsTableRow>
                                    <FactsTableRow label='Negation'>
                                        <RegisteredCheckBox label='True' name='negation' fullWidth />
                                    </FactsTableRow>
                                </FactsTable>

                                <DialogActions>
                                    <Button onClick={handleCloseDialog}>Stäng</Button>
                                    <Button
                                        onClick={() => {
                                            void handleSubmitForm()
                                        }}
                                        disabled={
                                            !['sv', 'en'].includes(formMethods.watch('language')) ||
                                            formMethods.watch('phrase').length === 0
                                        }
                                    >
                                        Lägg till
                                    </Button>
                                </DialogActions>
                            </FormProvider>
                        </DialogContent>
                    </Dialog>
                    <KeywordsGrid keywords={keywords} onUpdate={handleUpdateKeyword} onDelete={handleDeleteKeyword} />
                </CardContent>
            </Card>
        </>
    )
}

export default AdminKeywordFilterView
